import {
  GetConsultantBookingDocumentList,
  GetConsultantDocumentList,
  GetConsultantDocumentTypes,
  useUploadBlobMutation,
  useUploadConsultantDocumentsMutation
} from "@bonliva-crm/graphql";
import { useState } from "react";
import locale from "@bonliva-crm/locale";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";

type Params = {
  consultantId: string;
};

export function useFileUploader({ consultantId }: Params) {
  const [files, setFiles] = useState<{ file: File; blobRef: string }[]>([]);
  const [fileUploadResetCount, setFileUploadResetCount] = useState(0);
  const { showError, showSuccess } = useSnackbarsPipeline();
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [uploadBlobMutation] = useUploadBlobMutation({});
  const [uploadConsultantDocumentsMutation, uploadConsultantDocumentsData] =
    useUploadConsultantDocumentsMutation({});

  const onFileUpload = (file: File) => {
    uploadBlobMutation({
      variables: {
        file: file
      },
      onCompleted: (data) => {
        setFiles((prev) => [...prev, { file, blobRef: data.uploadBlob }]);
      },
      onError: (error) => {
        showError(locale.FileUploadFail);
        console.error(error);
      }
    });
  };

  const onFileRemove = (file: File) => {
    setFiles((prev) => prev.filter((f) => f.file !== file));
  };

  function clearFiles() {
    setFiles([]);
    setDocumentType(null);
    setFileUploadResetCount((prev) => prev + 1);
  }

  const submitFiles = () => {
    if (!documentType) {
      showError(locale.ChooseDocumentType);
      return;
    }

    const fileRefs = files.map((f) => f.blobRef);

    uploadConsultantDocumentsMutation({
      variables: {
        consultantId,
        documentType: documentType,
        blobRefs: fileRefs
      },
      onError: (error) => {
        showError(locale.FileUploadFail);
        console.error(error);
      },
      onCompleted: () => {
        showSuccess(locale.FileUploadSuccess);
        clearFiles();
      },
      refetchQueries: [
        GetConsultantDocumentList.OperationName,
        GetConsultantBookingDocumentList.OperationName,
        GetConsultantDocumentTypes.OperationName
      ]
    });
  };

  return {
    files,
    fileUploadResetCount,
    onFileUpload,
    onFileRemove,
    submitFiles,
    submitFilesData: uploadConsultantDocumentsData,
    clearFiles,
    documentType,
    setDocumentType
  };
}
